import React, { useEffect } from 'react'
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet'
import { alertKey } from './alertKey'

// Hazard-level colors, matching the card accents.
const LEVEL_COLOR = { 0: '#2e7d32', 1: '#f57f17', 2: '#c62828' }
const DEFAULT_CENTER = [39.0048687, -77.4303391]

// Pans the map to `point`. On an explicit selection we also zoom in far enough for the
// event to actually be locatable (a range-wide view can sit at zoom 11 or lower), but we
// never zoom back out — if the user is already closer in, their zoom is kept.
const Recenter = ({ point, focused }) => {
  const map = useMap()
  useEffect(() => {
    if (!point) return
    const zoom = focused ? Math.max(map.getZoom(), 15) : map.getZoom()
    map.flyTo(point, zoom, { duration: 0.5 })
  }, [point && point[0], point && point[1], focused]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

// A focused alert map: one dot per detection, colored by hazard level; click → detail.
// `alerts` is the [counter, alertObj] tuple list from the store. Selecting an alert (from
// the list or the map) snaps the map to it and rings that marker.
export const AlarmMap = ({ alerts, typeDict, onSelect, selectedKey }) => {
  const points = (alerts || [])
    .map(([, a]) => a)
    .filter((a) => a && a.location && a.location.latitude != null && a.location.longitude != null)

  const coords = (a) => [Number(a.location.latitude), Number(a.location.longitude)]

  // Follow the selected alert; with nothing selected, fall back to the first one.
  const selected = points.find((a) => alertKey(a) === selectedKey)
  const target = selected || points[0]
  const center = target ? coords(target) : DEFAULT_CENTER

  return (
    <MapContainer center={center} zoom={11} scrollWheelZoom className="rz-alarm-map">
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {points.map((a, i) => {
        const isSelected = alertKey(a) === selectedKey
        return (
          <CircleMarker
            key={`${a.serial_number}-${a.detection_date}-${i}`}
            center={coords(a)}
            radius={isSelected ? 11 : 7}
            pathOptions={{
              color: isSelected ? '#12263a' : '#fff',
              weight: isSelected ? 3 : 1.5,
              fillColor: LEVEL_COLOR[a.trigger_level] || '#8a97a6',
              fillOpacity: 0.9,
            }}
            eventHandlers={{ click: () => onSelect(a) }}
          >
            <Tooltip>
              {(a.vehicle_name || a.serial_number)} · {typeDict?.[a.trigger_type] ?? a.trigger_type}
            </Tooltip>
          </CircleMarker>
        )
      })}
      <Recenter point={center} focused={!!selected} />
    </MapContainer>
  )
}

export default AlarmMap
